/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import {useDisclosure} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {
    useCustomer,
    useCustomerId,
    useProducts,
    useShopperContext,
    useUsid,
} from '@salesforce/commerce-sdk-react'
import ConfirmationModal from '../../../../components/confirmation-modal'
import {useIntl} from 'react-intl'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getOrderAvailabilityStatus} from '../../../../utils/order-availability-utils'
import {useCustomPreference} from '../../../../hooks/use-custom-preference'

/**
 * This Component determines if the provided products have become unavailable or out of stock or low stock that
 * can't be fulfilled and will prompt the users to remove them before proceeding any further
 *
 * @param productIds -  list of product ids to check for availability. This prop will be deprecated in the upcoming release.
 * Please use productItems prop
 * @param productItems -  basket product items. This will be ignored if productIds is passed
 * @param handleUnavailableProducts - callback function to handle what to do with unavailable products
 * @returns {JSX.Element} -  Conformation Modal Component
 *
 */
const UnavailableProductConfirmationModal = ({
    productIds = [],
    productItems = [],
    handleUnavailableProducts,
}) => {
    const unavailableProductIdsRef = useRef(null)
    const ids = productIds.length ? productIds : productItems.map((i) => i.productId)
    const {formatMessage} = useIntl()

    const {site} = useMultiSite()
    const preorderSaleLimits = useCustomPreference('preorderSaleLimits')
    const backorderSaleLimits = useCustomPreference('backorderSaleLimits')
    const customerId = useCustomerId()
    const {usid} = useUsid()
    const {data: shopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    const {data: customer} = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer},
    )

    const isCorporate = shopperContext?.customQualifiers?.isCorporate === 'true'
    const isEmployee = customer?.c_isEmployee

    useProducts(
        {
            parameters: {
                ids: ids?.join(','),
                allImages: false,
            },
            headers: {
                'x-debug': 'unav-prod-index',
            },
        },
        {
            enabled: ids?.length > 0,
            onSuccess: (result) => {
                const resProductIds = []
                const unOrderableIds = []

                result.data?.forEach((product) => {
                    const filterConditions = {
                        c_skuEnabledForCountry: 'true',
                    }

                    // when a product is unavailable, the getProducts will not return its product detail.
                    // we compare the response ids with the ones in basket to figure which product has become unavailable
                    resProductIds.push(product.id)

                    // when a product is orderable, but the quantity in the basket is more than the remaining stock
                    // we want to make sure it is removed before go to checkout page to avoid error when placing order
                    // we don't need to remove low stock/ out of stock from wishlist
                    if (productItems.length) {
                        const productItem = productItems.find(
                            (item) => item.productId === product.id,
                        )
                        // wishlist item will have the property type
                        const isWishlist = !!productItem?.type

                        const isBonus = productItem.priceAdjustments?.some(
                            (item) => item.appliedDiscount && item.appliedDiscount.type === 'bonus',
                        )
                        if (!isBonus) {
                            filterConditions['c_skuIsPDPOnly'] = 'false'
                        }
                        if (isCorporate) {
                            filterConditions['c_visibleByCorporate'] = 'true'
                        }
                        if (isEmployee) {
                            filterConditions['c_visibleByEmployee'] = 'true'
                        }
                        if (!isCorporate && !isEmployee) {
                            filterConditions['c_visibleByAll'] = 'true'
                        }

                        const isVisible = Object.keys(filterConditions).every(
                            (key) => product[key] === filterConditions[key],
                        )

                        // Check if product is preorder or backorder
                        const orderStatus = getOrderAvailabilityStatus(product)
                        const isPreorder = orderStatus.isPreorder
                        const isBackorder = orderStatus.isBackorder

                        let shouldFlagAsUnavailable = false

                        if (isPreorder || isBackorder) {
                            // For preorder/backorder, check against custom preference limits
                            const limits = isPreorder ? preorderSaleLimits : backorderSaleLimits
                            const sku = product.id
                            const maxQty = limits?.products?.[sku]

                            // Default to 2 if no custom preference is set
                            const maxQuantity = maxQty ? Number(maxQty) : 2

                            // If quantity exceeds limit, flag as unavailable
                            if (productItem?.quantity > maxQuantity) {
                                shouldFlagAsUnavailable = true
                            }
                        } else {
                            // Standard stock validation for non-preorder/backorder products
                            if (
                                !isVisible ||
                                !product.inventory?.orderable ||
                                (product.inventory?.orderable &&
                                    productItem?.quantity > product.inventory.stockLevel)
                            ) {
                                shouldFlagAsUnavailable = true
                            }
                        }

                        if (!isWishlist && shouldFlagAsUnavailable) {
                            unOrderableIds.push(product.id)
                        }
                    }
                })

                const unavailableProductIds = ids.filter(
                    (id) => !resProductIds.includes(id) || unOrderableIds.includes(id),
                )

                unavailableProductIdsRef.current = unavailableProductIds
            },
        },
    )

    const {onOpen, onClose, isOpen} = useDisclosure()

    useEffect(() => {
        if (unavailableProductIdsRef.current?.length > 0) {
            onOpen()
        }
    }, [unavailableProductIdsRef.current])

    return (
        <ConfirmationModal
            data-testid="unavailable-product-modal"
            onAlternateAction={async () => {
                onClose()
                await handleUnavailableProducts(unavailableProductIdsRef.current)
                unavailableProductIdsRef.current = null
            }}
            title={formatMessage({
                defaultMessage: 'Error',
                id: 'confirmation_modal.title',
            })}
            description={formatMessage({
                defaultMessage:
                    'The item is no longer available and it needs to be removed from the cart',
                id: 'confirmation_modal.description',
            })}
            isOpen={isOpen}
        />
    )
}

UnavailableProductConfirmationModal.propTypes = {
    productItems: PropTypes.array,
    productIds: PropTypes.array,
    handleUnavailableProducts: PropTypes.func,
}

export default UnavailableProductConfirmationModal
